import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Building2, ChevronRight, Crown, X, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CAVEAT = "HMRC Control is a decision-support tool. It does not file returns for you and is not a substitute for advice from an ICAEW / CIOT registered accountant. Always confirm figures before submission.";

const HmrcCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', company_number: '', utr: '', vat_number: '', paye_ref: '', company_type: 'ltd', year_end: '', incorporated_on: '', vat_registered: false, vat_stagger: 1, has_payroll: false, self_assessment: false, notes: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    try { const res = await axios.get(`${API}/api/hmrc/companies`, { withCredentials: true }); setCompanies(res.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await axios.post(`${API}/api/hmrc/companies`, form, { withCredentials: true }); setShowAdd(false); setForm({ name: '', company_number: '', utr: '', vat_number: '', paye_ref: '', company_type: 'ltd', year_end: '', incorporated_on: '', vat_registered: false, vat_stagger: 1, has_payroll: false, self_assessment: false, notes: '' }); fetchCompanies(); }
    catch (err) { console.error(err); } finally { setSaving(false); }
  };

  const filtered = companies.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.company_number?.includes(search));

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }} /></div>;

  const inputStyle = "w-full px-3 py-2 text-sm border rounded-sm focus:outline-none focus:ring-1 focus:ring-amber-300";
  const labelStyle = "text-[10px] uppercase tracking-wider block mb-1";

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="hmrc-companies-page">
      <div className="px-6 py-2" style={{ backgroundColor: '#FAF7F2', borderBottom: '1px solid #E8E0D4' }}>
        <p className="text-xs text-center" style={{ color: '#8B7355' }}>{CAVEAT}</p>
      </div>

      <header className="bg-white px-6 lg:px-10 py-6" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/hmrc')} className="p-1.5 rounded-sm hover:bg-amber-50 transition-colors"><Crown className="w-5 h-5" style={{ color: '#D4AF37' }} /></button>
            <h1 className="text-xl font-light tracking-tight" style={{ color: '#2C1A0E' }}>Companies</h1>
          </div>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-sm" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="add-company-btn">
            <Plus className="w-3.5 h-3.5" /> Add Company
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-6">
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#8B7355' }} />
          <input type="text" placeholder="Search companies..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border rounded-sm" style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="company-search" />
        </div>

        <div className="bg-white border rounded-sm" style={{ borderColor: '#E5E1DB' }}>
          {filtered.map((c, i) => (
            <button key={c.id} onClick={() => navigate(`/hmrc/companies/${c.id}`)}
              className="w-full px-5 py-4 flex items-center justify-between hover:bg-amber-50/30 transition-colors text-left"
              style={{ borderBottom: i < filtered.length - 1 ? '1px solid #F5F1EB' : 'none' }} data-testid={`company-${c.id}`}>
              <div className="flex items-center gap-4">
                <Building2 className="w-5 h-5" style={{ color: '#8B7355' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{c.name}</p>
                  <p className="text-xs mt-0.5 font-mono" style={{ color: '#8B7355' }}>
                    {[c.company_number && `#${c.company_number}`, c.utr && `UTR ${c.utr}`, c.vat_number && `VAT ${c.vat_number}`].filter(Boolean).join(' · ') || 'No details yet'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {c.vat_registered && <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium" style={{ backgroundColor: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A' }}>VAT</span>}
                {c.has_payroll && <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium" style={{ backgroundColor: '#EFF6FF', color: '#3b82f6', border: '1px solid #BFDBFE' }}>PAYE</span>}
                <ChevronRight className="w-4 h-4" style={{ color: '#8B7355' }} />
              </div>
            </button>
          ))}
          {filtered.length === 0 && <div className="px-5 py-12 text-center"><p className="text-sm" style={{ color: '#8B7355' }}>{search ? 'No companies match' : 'No companies added yet'}</p></div>}
        </div>
      </main>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(44,26,14,0.5)' }} onClick={() => setShowAdd(false)}>
          <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()} className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-white p-6 rounded-sm shadow-xl" data-testid="add-company-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#B8860B' }}>Add Company</h2>
              <button type="button" onClick={() => setShowAdd(false)}><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="space-y-4">
              <div><label className={labelStyle} style={{ color: '#8B7355' }}>Company Name *</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputStyle} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelStyle} style={{ color: '#8B7355' }}>Company Number</label>
                  <input value={form.company_number} onChange={e => setForm({ ...form, company_number: e.target.value })} className={`${inputStyle} font-mono`} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-co-number" /></div>
                <div><label className={labelStyle} style={{ color: '#8B7355' }}>UTR</label>
                  <input value={form.utr} onChange={e => setForm({ ...form, utr: e.target.value })} className={`${inputStyle} font-mono`} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-utr" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelStyle} style={{ color: '#8B7355' }}>VAT Number</label>
                  <input value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} className={`${inputStyle} font-mono`} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-vat" /></div>
                <div><label className={labelStyle} style={{ color: '#8B7355' }}>PAYE Reference</label>
                  <input value={form.paye_ref} onChange={e => setForm({ ...form, paye_ref: e.target.value })} className={`${inputStyle} font-mono`} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-paye" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelStyle} style={{ color: '#8B7355' }}>Year End</label>
                  <input type="date" value={form.year_end} onChange={e => setForm({ ...form, year_end: e.target.value })} className={inputStyle} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-year-end" /></div>
                <div><label className={labelStyle} style={{ color: '#8B7355' }}>Incorporated On</label>
                  <input type="date" value={form.incorporated_on} onChange={e => setForm({ ...form, incorporated_on: e.target.value })} className={inputStyle} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-incorporated" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelStyle} style={{ color: '#8B7355' }}>Type</label>
                  <select value={form.company_type} onChange={e => setForm({ ...form, company_type: e.target.value })} className={inputStyle} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-type">
                    <option value="ltd">Limited Company</option><option value="sole_trader">Sole Trader</option>
                  </select></div>
                <div><label className={labelStyle} style={{ color: '#8B7355' }}>VAT Stagger</label>
                  <select value={form.vat_stagger} onChange={e => setForm({ ...form, vat_stagger: parseInt(e.target.value) })} className={inputStyle} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-stagger">
                    <option value={1}>Group 1 (Mar/Jun/Sep/Dec)</option><option value={2}>Group 2 (Apr/Jul/Oct/Jan)</option><option value={3}>Group 3 (May/Aug/Nov/Feb)</option>
                  </select></div>
              </div>
              <div className="flex flex-wrap gap-4">
                {[['vat_registered', 'VAT Registered'], ['has_payroll', 'Has Payroll'], ['self_assessment', 'Self-Assessment']].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form[key]} onChange={e => setForm({ ...form, [key]: e.target.checked })} className="accent-amber-600" data-testid={`check-${key}`} />
                    <span className="text-xs" style={{ color: '#8B7355' }}>{label}</span>
                  </label>
                ))}
              </div>
              <div><label className={labelStyle} style={{ color: '#8B7355' }}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className={`${inputStyle} resize-none`} style={{ borderColor: '#E5E1DB', color: '#2C1A0E' }} data-testid="input-notes" /></div>
            </div>
            <button type="submit" disabled={saving || !form.name} className="w-full mt-5 py-2.5 text-xs uppercase tracking-wider font-medium rounded-sm disabled:opacity-50" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="submit-company">
              {saving ? 'Adding...' : 'Add Company & Generate Deadlines'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default HmrcCompanies;
